import React, { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Input from '@cloudscape-design/components/input';
import Toggle from '@cloudscape-design/components/toggle';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Flashbar, { FlashbarProps } from '@cloudscape-design/components/flashbar';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CopyableText } from '../../components/CopyableText';
import { getStatusType } from '../../utils/statusUtils';
import { formatTimestamp, getStoredTimeFormat } from '../../utils/timeFormat';
import { updateStore } from '../../api/stores';
import { getConfig } from '../../config';

interface KeyStrategy {
  fields?: string[];
  delimiter?: string;
}

interface StoreData {
  name: string;
  arn?: string;
  domain?: string;
  status?: string;
  createdAt?: string;
  streamKey?: KeyStrategy;
  deletionProtection?: boolean;
  endpoints?: {
    events?: string;
    lineage?: string;
  };
}

interface ConfigurationTabProps {
  store: StoreData;
}

export function ConfigurationTab({ store }: ConfigurationTabProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [domain, setDomain] = useState(store.domain || '');
  const [deletionProtection, setDeletionProtection] = useState(store.deletionProtection ?? true);
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);

  const displayStatus = store.status || 'UNKNOWN';
  const isActive = store.status === 'ACTIVE';

  const streamKeyConfig = store.streamKey;
  const streamKeyDisplay = streamKeyConfig?.fields
    ? `${streamKeyConfig.fields.join(` ${streamKeyConfig.delimiter || '#'} `)}` 
    : 'Caller-provided (opaque stream ID)';

  const mutation = useMutation({
    mutationFn: () => updateStore(store.name, { domain, deletionProtection }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', store.name] });
      setEditing(false);
      setFlash([{ type: 'success', content: 'Store updated.', dismissible: true, onDismiss: () => setFlash([]) }]);
    },
    onError: (err: Error) => {
      setFlash([{ type: 'error', content: err.message, dismissible: true, onDismiss: () => setFlash([]) }]);
    },
  });

  const handleCancel = () => {
    setDomain(store.domain || '');
    setDeletionProtection(store.deletionProtection ?? true);
    setEditing(false);
  };

  return (
    <SpaceBetween size="l">
      <Flashbar items={flash} />

      <Container
        header={
          <Header
            variant="h2"
            actions={
              isActive && !editing ? (
                <Button onClick={() => { setDomain(store.domain || ""); setDeletionProtection(store.deletionProtection ?? true); setEditing(true); }}>Edit</Button>
              ) : editing ? (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={handleCancel}>Cancel</Button>
                  <Button variant="primary" loading={mutation.isPending} onClick={() => mutation.mutate()}>
                    Save changes
                  </Button>
                </SpaceBetween>
              ) : undefined
            }
          >
            Store details
          </Header>
        }
      >
        <ColumnLayout columns={2} variant="text-grid">
          <SpaceBetween size="l">
            <div>
              <Box variant="awsui-key-label">Name</Box>
              <Box>{store.name}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">ARN</Box>
              <CopyToClipboard
                variant="inline"
                textToCopy={store.arn || `arn:aws:eventstore:${getConfig().region}:${getConfig().tenantId}:store/${store.name}`}
                copyButtonAriaLabel="Copy ARN"
                copySuccessText="ARN copied"
                copyErrorText="Failed to copy"
              />
            </div>
            <div>
              <Box variant="awsui-key-label">Domain</Box>
              {editing ? (
                <Input value={domain} onChange={({ detail }) => setDomain(detail.value)} />
              ) : (
                <Box>{store.domain || '—'}</Box>
              )}
            </div>
            <div>
              <Box variant="awsui-key-label">Status</Box>
              <StatusIndicator type={getStatusType(displayStatus)}>
                {displayStatus}
              </StatusIndicator>
            </div>
          </SpaceBetween>
          <SpaceBetween size="l">
            <div>
              <Box variant="awsui-key-label">Created</Box>
              <Box>{formatTimestamp(store.createdAt, getStoredTimeFormat())}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Deletion protection</Box>
              {editing ? (
                <Toggle
                  checked={deletionProtection}
                  onChange={({ detail }) => setDeletionProtection(detail.checked)}
                >
                  {deletionProtection ? 'Enabled' : 'Disabled'}
                </Toggle>
              ) : (
                <Box>{(store.deletionProtection ?? true) ? 'Enabled' : 'Disabled'}</Box>
              )}
            </div>
            <div>
              <Box variant="awsui-key-label">Stream key</Box>
              <Box>{streamKeyDisplay}</Box>
            </div>
          </SpaceBetween>
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h2">Endpoints</Header>}>
        <ColumnLayout columns={1} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Write events</Box>
            <CopyableText text={`POST ${store.endpoints?.events || `/v1/stores/${store.name}/events`}`} />
          </div>
          <div>
            <Box variant="awsui-key-label">Read events</Box>
            <CopyableText text={`GET ${store.endpoints?.events || `/v1/stores/${store.name}/events`}?stream=...`} />
          </div>
          <div>
            <Box variant="awsui-key-label">Event lineage</Box>
            <CopyableText text={store.endpoints?.lineage || `/v1/stores/${store.name}/lineage`} />
          </div>
        </ColumnLayout>
      </Container>
    </SpaceBetween>
  );
}
